import { useContext, useEffect, useMemo, useState } from 'react';
import greenCircleTick from '../../../../images/green-circle-tick.png';
import redCircleCross from '../../../../images/red-circle-cross.png';
import { getLoadTypeFromDescription } from '../../../../utils/format';
import BPModal from '../../../BusinessPartners/BPModal';
import OverLayWrapper from '../../../Overlay';
import { StoreContext } from '../../../Store';
import AuthModal from '../../AuthModal';
import { QAStoreContext } from '../QAStore';
import './FloatingBar.css';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getValueFromID, qaDutyLoginRequired } from '../utils';
import { useSecurity } from '../../../Security/security';
import TooltipHover from '../../../Tooltip/Tooltip';
import { useDropdownOptions } from '../../queryHooks/useDropdownOptions';
import SaveAsModal from '../../Modals/SaveAsModal';

const FOOTER_SPACER_CLASS = 'floating-bar-spacer';

const countActiveNotes = (notes) => (notes ?? []).filter((note) => !note.deleted).length;

const NotesButton = ({ children, onClick, totalNotes = 0 }) => {
  const buttonClasses = ['button-text-link'];
  if (totalNotes > 0) {
    buttonClasses.push('contains-notes');
  }

  return (
    <div>
      <button className={buttonClasses.join(' ')} onClick={onClick}>
        {children} ({totalNotes})
      </button>
    </div>
  );
};

const NotesModal = ({ id, show, setShow, setData, notes, setNotes }) => {
  return (
    <BPModal
      show={show}
      customerId={id}
      onHide={() => setShow(false)}
      notesData={notes}
      handleChangeNotesData={(data) => {
        setNotes(data);
        setData((prev) => ({ ...prev, totalNotes: countActiveNotes(data) }));
      }}
      loadDataInitially
    />
  );
};

const renderTooltip = ({ show, ...props }) => {
  return show ? (
    <Tooltip id="button-tooltip" {...props}>
      Requires a user with duty permissions.
    </Tooltip>
  ) : (
    <div {...props}></div>
  );
};

const getLoginPopupMessages = (errors, duty) => {
  let result = {};

  if (errors && duty) {
    result = {
      title: 'Confirm QA AND duty Override',
      mainMessage: 'You are attempting to submit a load with duty liability AND override QA settings.',
      instructionMessage:
        'To continue with the current shipment details, please enter your username and password, or cancel to update the shipment.',
    };
  } else if (errors) {
    result = {
      title: 'Confirm QA Override',
      mainMessage: 'You are attempting to override QA settings.',
      instructionMessage:
        'To continue with the current shipment details, please enter your username and password, or cancel to update the shipment.',
    };
  } else if (duty) {
    result = {
      title: 'Confirm Duty Override',
      mainMessage: 'You are submitting a load with duty liability.',
      instructionMessage:
        'To continue with the current shipment details, please enter your username and password, or cancel to update the shipment.',
    };
  }

  return result;
};

const FloatingBar = ({
  isVisible,
  submitLoadAsDraft,
  submitLoad,
  isComplianceChecklistValid,
  isDataCheckValid,
  isFinancialsValid,
  headerErrors,
  productItemsHasErrors,
  readOnly,
  onOverviewClick,
  disableSaveButtons,
  portModelID,
  hasExciseGoods,
  hasQuota,
}) => {
  const [confirmAndOverrideErrors, setConfirmAndOverrideErrors] = useState(false);

  const { staticDropdowns } = useDropdownOptions();
  const { portProcesses } = staticDropdowns.data || {};

  const store = useContext(StoreContext);
  const qaStore = useContext(QAStoreContext);

  const jiraTicket = store.jiraTicket[0];
  const portalRef = store.portalRef[0] || 'N/A';
  const loadTypeDescription = store.loadTypeDescription[0];
  const newLoadFromFile = store.newLoadFromFile[0];
  const newLoadFileData = store.newLoadFileData[0];
  const newLoadParent = store.newLoadParent[0];
  const isFromFileUpload = !!newLoadFromFile || !!newLoadFileData?.Headers;

  const loadTypeDesc = loadTypeDescription || (isFromFileUpload ? 'FileUpload' : 'Build Your Load');
  const dataSource = getLoadTypeFromDescription(loadTypeDesc);

  const jiraUrl = jiraTicket ? `https://eoriuklimited.atlassian.net/browse/${jiraTicket}` : null;

  const statValueWarning = qaStore.statValueWarning;
  const confirmDutyRequired = qaStore.confirmDutyRequired[0];

  //HARD Errors - Errors on the Header or Items tab and user CANNOT override them
  //SOFT Errors - Other errors and user CAN override them
  const hardErrors = headerErrors?.length || productItemsHasErrors;
  const softErrors = !isComplianceChecklistValid || !isDataCheckValid || !isFinancialsValid || statValueWarning;
  const isLoadInvalid = hardErrors || softErrors;

  // customer and neenor notes values + state
  const [showConsigneeNotes, setShowConsigneeNotes] = useState(false);
  const [consigneeNotes, setConsigneeNotes] = useState([]);
  const [showConsignorNotes, setShowConsignorNotes] = useState(false);
  const [consignorNotes, setConsignorNotes] = useState([]);
  const [showCustomerNotes, setShowCustomerNotes] = useState(false);
  const [customerNotes, setCustomerNotes] = useState([]);
  const [submissionSelected, setSubmissionSelected] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const [customerData, setCustomerData] = store.QACustomerData;
  const [consigneeData, setConsigneeData] = store.QAConsigneeData;
  const [consignorData, setConsignorData] = store.QAConsignorData;

  const [isQaAuthInProgress, setIsQaAuthInProgress] = useState(false);
  const [showQaAuthModal, setShowQaAuthModal] = useState(false);

  const { validateRole } = useSecurity();
  const isDutyUser = validateRole({ isDutyUser: true });
  const potentialLiability = qaStore.potentialLiabilityGBP[0];
  const isGbImport = store.GBimport[0];
  const isControlled = store.controlledGoods[0] === 'Y';
  const isRowToGB = Number(store.directionSelected[0]) === 13;
  const selectedDocuments = store.docTypes[0];
  const portValue = getValueFromID(portProcesses, portModelID);

  const [openSaveAs, setOpenSaveAs] = useState(false);

  const dutyStatus = useMemo(
    () =>
      qaDutyLoginRequired({
        isDutyUser,
        potentialLiability,
        isGbImport,
        isControlled,
        isRowToGB,
        selectedDocuments,
        portValue,
        hasExciseGoods,
      }),
    [hasExciseGoods, isControlled, isDutyUser, isGbImport, isRowToGB, portValue, potentialLiability, selectedDocuments]
  );

  useEffect(() => {
    if (isVisible) {
      // uncheck this when user navigates to this tab
      setConfirmAndOverrideErrors(false);
    }

    // adding padding to the footer (so the floating bar does not overlap it)
    const footer = document.querySelector('footer.footer');
    if (footer) {
      if (isVisible) {
        footer.classList.add(FOOTER_SPACER_CLASS);
      } else {
        footer.classList.remove(FOOTER_SPACER_CLASS);
      }
    }

    // remove class on dismount
    return () => {
      if (footer) {
        footer.classList.remove(FOOTER_SPACER_CLASS);
      }
    };
  }, [isVisible]);

  const handleQaAuthConfirm = async () => {
    if (submitLoad) {
      const mockEvent = {
        preventDefault: () => {},
      };
      setIsQaAuthInProgress(true);
      await submitLoad(mockEvent, true);
      setIsQaAuthInProgress(false);
    }

    setShowQaAuthModal(false);
    setConfirmAndOverrideErrors(true);
  };

  const handleQaAuthCancel = () => {
    setShowQaAuthModal(false);
  };

  const handleSubmitLoadClick = () => {
    if (confirmAndOverrideErrors && (softErrors || dutyStatus.LOGIN)) {
      // Show the QA Override modal
      setShowQaAuthModal(true);
    } else if (!isLoadInvalid) {
      // Submit load directly if no errors or override not needed
      setSubmissionSelected(true);
      if (submitLoad) {
        const mockEvent = {
          preventDefault: () => {},
        };
        submitLoad(mockEvent);
      }
    }
  };

  const handleSaveAsDraftClick = async (event) => {
    setIsSavingDraft(true);
    await submitLoadAsDraft(event);
    setIsSavingDraft(false);
  };

  const WarningMessage = () => {
    return (
      <>
        {headerErrors.length > 0 && (
          <p>You cannot submit this load until all required fields in the Header are completed.</p>
        )}
        {productItemsHasErrors && (
          <p>You cannot submit this load until all required fields in the Items are completed.</p>
        )}
      </>
    );
  };

  const MessageOrIcon = () => {
    if (hardErrors) {
      return (
        <OverLayWrapper placement="top" popoverContent={<WarningMessage />}>
          <div className="warning" style={{ width: '1.5rem', height: '1.5rem' }} />
        </OverLayWrapper>
      );
    }

    if ((softErrors || dutyStatus.LOGIN) && !dutyStatus.BLOCKED) {
      const checkboxStyles = confirmDutyRequired ? { opacity: 0.3 } : {};
      return (
        <div className="d-flex align-items-center gap-2 user-select-none pointer">
          <label htmlFor="overrideErrors" style={checkboxStyles}>
            Tick to confirm and override errors or duty
          </label>
          <input
            type="checkbox"
            id="overrideErrors"
            checked={confirmAndOverrideErrors}
            onChange={(e) => setConfirmAndOverrideErrors(e.target.checked)}
            disabled={confirmDutyRequired}
            style={checkboxStyles}
          />
          {confirmDutyRequired && (
            <TooltipHover>
              {store.customsMopValue[0] !== 'Check with customer' && (
                <p>EORI&apos;s Deferment account is being used as the Customs MoP.</p>
              )}
              <p>Please select an option in the Customs MoP to continue</p>
            </TooltipHover>
          )}
        </div>
      );
    }

    return null;
  };

  const submitDisabled =
    ((isLoadInvalid || dutyStatus.LOGIN) && !confirmAndOverrideErrors) ||
    disableSaveButtons ||
    dutyStatus.BLOCKED ||
    submissionSelected ||
    (confirmDutyRequired && !isDutyUser) ||
    (confirmDutyRequired && hasQuota);

  const showCustomerNotesButton = !!(customerData.id && customerData.totalNotes > 0);
  const showConsignorNotesButton = !!(consignorData.id && consignorData.totalNotes > 0);
  const showConsigneeNotesButton = !!(consigneeData.id && consigneeData.totalNotes > 0);

  return (
    <>
      <NotesModal
        id={consigneeData.id}
        show={showConsigneeNotes}
        setShow={setShowConsigneeNotes}
        setData={setConsigneeData}
        notes={consigneeNotes}
        setNotes={setConsigneeNotes}
      />
      <NotesModal
        id={consignorData.id}
        show={showConsignorNotes}
        setShow={setShowConsignorNotes}
        setData={setConsignorData}
        notes={consignorNotes}
        setNotes={setConsignorNotes}
      />
      <NotesModal
        id={customerData.id}
        show={showCustomerNotes}
        setShow={setShowCustomerNotes}
        setData={setCustomerData}
        notes={customerNotes}
        setNotes={setCustomerNotes}
      />

      <AuthModal
        show={showQaAuthModal}
        onHide={handleQaAuthCancel}
        onSuccess={handleQaAuthConfirm}
        isLoading={isQaAuthInProgress}
        {...getLoginPopupMessages(isLoadInvalid, dutyStatus.LOGIN)}
      />

      <div className="floating-bar d-flex justify-content-between align-items-center position-fixed fixed-bottom px-4">
        <div className="quality-checklist d-flex align-items-center gap-2">
          <span>Quality Checklist</span>{' '}
          <img src={hardErrors || softErrors ? redCircleCross : greenCircleTick} width={48} alt="Quality Checklist" />
        </div>

        <div className="qa-job-info">
          <table role="presentation">
            <tbody>
              <tr>
                <td>Parent:</td>
                <td data-testid="qa-job-details-parent">{newLoadParent?.parentName || ''}</td>
              </tr>
              <tr>
                <td>CABIE Reference:</td>
                <td data-testid="qa-job-details-portal-ref">{portalRef}</td>
              </tr>
              <tr>
                <td>Jira Ref:</td>
                <td data-testid="qa-job-details-jira-ref">
                  {jiraTicket ? (
                    <a
                      href={jiraUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0052CC', textDecoration: 'underline' }}
                    >
                      {jiraTicket}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </td>
              </tr>
              <tr>
                <td>Data Source:</td>
                <td data-testid="qa-job-details-data-source">{dataSource}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="qa-notes">
          {showCustomerNotesButton && (
            <NotesButton totalNotes={customerData.totalNotes} onClick={() => setShowCustomerNotes(true)}>
              Customer Notes
            </NotesButton>
          )}
          {showConsignorNotesButton && (
            <NotesButton totalNotes={consignorData.totalNotes} onClick={() => setShowConsignorNotes(true)}>
              Consignor Notes
            </NotesButton>
          )}
          {showConsigneeNotesButton && (
            <NotesButton totalNotes={consigneeData.totalNotes} onClick={() => setShowConsigneeNotes(true)}>
              Consignee Notes
            </NotesButton>
          )}
        </div>
        {!readOnly && (
          <div className="d-flex gap-4 align-items-center">
            {!disableSaveButtons && <MessageOrIcon />}
            {hardErrors && (
              <button className="qa-button black" onClick={onOverviewClick}>
                Overview
              </button>
            )}
            {/* Save As modal wiring lives in CABS-3825; draft save remains available here */}
            <button
              className="qa-button green"
              onClick={() => setOpenSaveAs(true)}
              disabled={disableSaveButtons || submissionSelected || isSavingDraft}
            >
              Save as
            </button>
            <OverlayTrigger placement="top" overlay={(props) => renderTooltip({ ...props, show: dutyStatus.BLOCKED })}>
              <button className="qa-button" onClick={handleSubmitLoadClick} disabled={submitDisabled || isSavingDraft}>
                Submit
              </button>
            </OverlayTrigger>
          </div>
        )}
      </div>

      <SaveAsModal
        show={openSaveAs}
        onHide={() => setOpenSaveAs(false)}
        onSave={handleSaveAsDraftClick}
        saveLoadAs={qaStore.saveLoadAs[0]}
        setSaveLoadAs={qaStore.saveLoadAs[1]}
      />
    </>
  );
};

export default FloatingBar;
